import type L from 'leaflet';
import { isLowDataMode } from './lowDataMode';
import { selectedLotStore } from '../data/plots';

// We intentionally avoid `import * as Cesium from 'cesium'` here so the
// 2D bundle (and the offline PWA precache) stays Cesium-free. We only
// read values off `window.cesiumViewer` when the active mode is 3D —
// `camera.positionCartographic` already gives us radians, so we don't
// need Cesium's math helpers either.
type CartographicLike = { longitude: number; latitude: number; height: number };
type CesiumCameraLike = {
  positionCartographic: CartographicLike;
  heading: number;
  pitch: number;
};
type CesiumViewerLike = { camera: CesiumCameraLike };

type WindowExt = Window & {
  cesiumViewer?: CesiumViewerLike;
  leafletMap?: L.Map & { getBearing?: () => number };
};

const RAD_TO_DEG = 180 / Math.PI;

/**
 * Build a shareable URL that captures the current viewer state. In 2D mode
 * we encode `lon/lat/z/b` (Leaflet center + zoom + bearing). In 3D mode we
 * encode `lon/lat/h/heading/pitch` (Cesium camera). Both also carry the
 * currently selected `lot` if any. Returns null if the active viewer
 * hasn't initialised yet.
 */
export function snapshotCurrentViewUrl(): string | null {
  const url = new URL(window.location.origin + window.location.pathname);
  const selectedLot = selectedLotStore.get();

  if (isLowDataMode()) {
    const map = (window as WindowExt).leafletMap;
    if (!map) return null;
    const c = map.getCenter();
    url.searchParams.set('mode', '2d');
    url.searchParams.set('lon', c.lng.toFixed(6));
    url.searchParams.set('lat', c.lat.toFixed(6));
    url.searchParams.set('z', String(map.getZoom()));
    const bearing = typeof map.getBearing === 'function' ? map.getBearing() : 0;
    if (bearing) url.searchParams.set('b', bearing.toFixed(2));
    if (selectedLot) url.searchParams.set('lot', selectedLot.id);
    return url.toString();
  }

  const v = (window as WindowExt).cesiumViewer;
  if (!v) return null;
  const cam = v.camera;
  const carto = cam.positionCartographic;
  url.searchParams.set('mode', '3d');
  url.searchParams.set('lon', (carto.longitude * RAD_TO_DEG).toFixed(6));
  url.searchParams.set('lat', (carto.latitude * RAD_TO_DEG).toFixed(6));
  url.searchParams.set('h', carto.height.toFixed(1));
  url.searchParams.set('heading', (cam.heading * RAD_TO_DEG).toFixed(2));
  url.searchParams.set('pitch', (cam.pitch * RAD_TO_DEG).toFixed(2));
  if (selectedLot) url.searchParams.set('lot', selectedLot.id);
  return url.toString();
}
